import React, { useState } from 'react';
import { alpha, Box, Button, ButtonBase, Collapse, IconButton, Stack, Typography, useTheme } from '@mui/material';
import { CheckCircle, Close, ExpandLess, ExpandMore, InfoOutlined, TipsAndUpdatesOutlined } from '@mui/icons-material';

/** "1" once the reader has folded the tips away (they start open on a first visit), or dismissed them. */
const CLOSED_KEY = 'ltb.boardScanTips.closed';
const DISMISSED_KEY = 'ltb.boardScanTips.dismissed';
const readFlag = (key: string) => {
  try {
    return localStorage.getItem(key) === '1';
  } catch {
    return false;
  }
};
const writeFlag = (key: string, on: boolean) => {
  try {
    if (on) localStorage.setItem(key, '1');
    else localStorage.removeItem(key);
  } catch {
    // blocked storage: remembered for this visit only
  }
};

const TIPS: React.ReactNode[] = [
  <>
    <b>Win, top 3, top 5, top 10 and head-to-head matchups.</b> Group matchups, stage winners and props are skipped.
  </>,
  <>
    <b>Headers help, but aren't required.</b> Without them we work the market out from the prices, and close calls
    (Top 3 or Top 5?) are priced cautiously. With the title or column headers (Win, Top 5, Top 10) in the shot, every
    price is read as exactly the right bet.
  </>,
  <>
    <b>Full driver names and prices,</b> nothing cut off at the edges. Long boards: take a few screenshots (up to 4).
  </>,
  <>
    <b>Boards for this race only.</b>
  </>,
];

const ROWS: [string, string, string, string][] = [
  ['Kyle Larson', '+450', '-150', '-400'],
  ['Denny Hamlin', '+500', '-130', '-350'],
  ['Ryan Blaney', '+650', '+110', '-250'],
];

/** A tiny generic odds board for the good / bad examples: with or without the market headers. */
const MiniBoard: React.FC<{ headers: boolean }> = ({ headers }) => {
  const theme = useTheme();
  const dark = theme.palette.mode === 'dark';
  const ok = theme.palette.success.main;
  const cell = { fontSize: 10.5, fontVariantNumeric: 'tabular-nums', textAlign: 'center' as const, fontWeight: 700 };
  return (
    <Box
      aria-hidden
      sx={{
        borderRadius: 1.5,
        overflow: 'hidden',
        bgcolor: dark ? '#15181c' : '#f4f5f7',
        border: 1,
        borderColor: 'divider',
        color: dark ? '#e8eaed' : '#1b1d21',
        position: 'relative',
      }}
    >
      {headers ? (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: '1.6fr 1fr 1fr 1fr',
            alignItems: 'center',
            px: 1,
            py: 0.6,
            outline: `2px dashed ${ok}`,
            outlineOffset: -3,
            borderRadius: 1,
            bgcolor: alpha(ok, 0.12),
          }}
        >
          <Typography sx={{ fontSize: 10, fontWeight: 700, opacity: 0.7 }}>Martinsville</Typography>
          {['Win', 'Top 5', 'Top 10'].map((h) => (
            <Typography key={h} sx={{ ...cell, fontSize: 10, color: ok }}>
              {h}
            </Typography>
          ))}
        </Box>
      ) : (
        <Box
          sx={{
            height: 4,
            background: `repeating-linear-gradient(90deg, ${theme.palette.divider} 0 6px, transparent 6px 10px)`,
          }}
        />
      )}
      {ROWS.map(([name, ...odds]) => (
        <Box
          key={name}
          sx={{
            display: 'grid',
            gridTemplateColumns: '1.6fr 1fr 1fr 1fr',
            alignItems: 'center',
            px: 1,
            py: 0.55,
            borderTop: 1,
            borderColor: 'divider',
          }}
        >
          <Typography sx={{ fontSize: 10.5, fontWeight: 600 }} noWrap>
            {name}
          </Typography>
          {odds.map((o, i) => (
            <Box key={i} sx={{ ...cell, mx: 0.25, py: 0.2, borderRadius: 0.75, bgcolor: dark ? '#22262c' : '#e6e8ec' }}>
              {o}
            </Box>
          ))}
        </Box>
      ))}
    </Box>
  );
};

const Example: React.FC<{ good: boolean }> = ({ good }) => (
  <Box sx={{ flex: 1, minWidth: 0 }}>
    <MiniBoard headers={good} />
    <Typography
      variant="caption"
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 0.5,
        mt: 0.75,
        fontWeight: 600,
        color: good ? 'success.main' : 'text.secondary',
      }}
    >
      {good ? <CheckCircle sx={{ fontSize: 15 }} /> : <InfoOutlined sx={{ fontSize: 15, color: 'warning.main' }} />}
      {good ? 'Headers in the shot: read exactly' : 'No headers: worked out from the prices'}
    </Typography>
  </Box>
);

/** The board scanner's "Tips for a good scan". On the upload screen: open on a first visit, folded or dismissed for
 * good once the reader does so (a small link brings them back). As a reminder on the results (after a shaky read):
 * folded, with no dismiss, whatever was saved. */
export const BoardScanTips: React.FC<{ reminder?: boolean }> = ({ reminder }) => {
  const [open, setOpen] = useState(() => !reminder && !readFlag(CLOSED_KEY));
  const [dismissed, setDismissed] = useState(() => !reminder && readFlag(DISMISSED_KEY));
  const toggle = () => {
    if (!reminder) writeFlag(CLOSED_KEY, open);
    setOpen(!open);
  };
  const dismiss = (gone: boolean) => {
    writeFlag(DISMISSED_KEY, gone);
    setDismissed(gone);
    if (!gone) {
      writeFlag(CLOSED_KEY, false);
      setOpen(true);
    }
  };
  if (dismissed) {
    return (
      <Button
        size="small"
        startIcon={<TipsAndUpdatesOutlined />}
        onClick={() => dismiss(false)}
        sx={{ alignSelf: 'flex-end', color: 'text.secondary', mb: -1 }}
      >
        Scan tips
      </Button>
    );
  }
  return (
    <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 2, overflow: 'hidden' }}>
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <ButtonBase
          onClick={toggle}
          aria-expanded={open}
          sx={{
            flex: 1,
            justifyContent: 'flex-start',
            gap: 1,
            px: 1.5,
            py: 1.1,
            textAlign: 'left',
            '&:hover': { bgcolor: 'action.hover' },
          }}
        >
          <TipsAndUpdatesOutlined sx={{ fontSize: 19, color: 'warning.main' }} />
          <Typography variant="body2" sx={{ fontWeight: 700, flex: 1 }}>
            {reminder ? 'Tips for a cleaner scan' : 'Tips for a good scan'}
          </Typography>
          {!open && (
            <Typography variant="caption" color="text.secondary" sx={{ display: { xs: 'none', sm: 'block' } }}>
              {reminder ? 'Headers in the shot read best' : 'Win, top 3, top 5, top 10 and matchups'}
            </Typography>
          )}
          {open ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
        </ButtonBase>
        {!reminder && (
          <IconButton size="small" aria-label="Dismiss tips" onClick={() => dismiss(true)} sx={{ mx: 0.5 }}>
            <Close fontSize="small" />
          </IconButton>
        )}
      </Box>
      <Collapse in={open}>
        <Box sx={{ px: 1.5, pb: 1.75, pt: 0.25 }}>
          <Box component="ul" sx={{ m: 0, pl: 2.25, display: 'flex', flexDirection: 'column', gap: 0.6 }}>
            {TIPS.map((t, i) => (
              <Typography
                key={i}
                component="li"
                variant="body2"
                color="text.secondary"
                sx={{ '& b': { color: 'text.primary', fontWeight: 600 } }}
              >
                {t}
              </Typography>
            ))}
          </Box>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ mt: 1.75 }}>
            <Example good />
            <Example good={false} />
          </Stack>
        </Box>
      </Collapse>
    </Box>
  );
};
